import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Grid,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Avatar,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  CheckCircle as PresentIcon,
  Cancel as AbsentIcon,
  Schedule as LateIcon,
  EventAvailable as AttendanceIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  Today as TodayIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import { motion } from 'framer-motion';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';

const AttendanceManagement = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [tabValue, setTabValue] = useState(0);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [openMarkDialog, setOpenMarkDialog] = useState(false);
  const [stats, setStats] = useState({
    totalPresent: 0,
    totalAbsent: 0,
    totalLate: 0,
    attendanceRate: 0
  });

  const [attendanceData, setAttendanceData] = useState({});

  useEffect(() => {
    fetchClasses();
    fetchStats();
  }, []);

  useEffect(() => {
    if (selectedClass) {
      fetchStudents();
      fetchAttendance();
    }
  }, [selectedClass, selectedDate]);

  const fetchClasses = async () => {
    try {
      const response = await axios.get('/api/classes');
      setClasses(response.data || []);
    } catch (error) {
      enqueueSnackbar('Failed to fetch classes', { variant: 'error' });
    }
  };

  const fetchStudents = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/attendance/students/${selectedClass}`);
      if (response.data.success) {
        setStudents(response.data.data || []);
        // Initialize attendance data
        const initialData = {};
        response.data.data.forEach(student => {
          initialData[student.student_id] = {
            status: 'present',
            remarks: ''
          };
        });
        setAttendanceData(initialData);
      }
    } catch (error) {
      enqueueSnackbar('Failed to fetch students', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchAttendance = async () => {
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const response = await axios.get(`/api/attendance/${selectedClass}/${dateStr}`);
      if (response.data.success) {
        setAttendance(response.data.data || []);
        // Update attendance data with existing records
        const existingData = {};
        response.data.data.forEach(record => {
          existingData[record.student_id] = {
            status: record.status,
            remarks: record.remarks || ''
          };
        });
        setAttendanceData(prev => ({ ...prev, ...existingData }));
      }
    } catch (error) {
      console.error('Failed to fetch attendance:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get('/api/attendance/stats');
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleMarkAttendance = async () => {
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const attendanceRecords = Object.entries(attendanceData).map(([studentId, data]) => ({
        student_id: parseInt(studentId),
        status: data.status,
        remarks: data.remarks
      }));

      const response = await axios.post('/api/attendance/mark', {
        class_id: selectedClass,
        date: dateStr,
        attendance: attendanceRecords
      });

      if (response.data.success) {
        enqueueSnackbar('Attendance marked successfully', { variant: 'success' });
        setOpenMarkDialog(false);
        fetchAttendance();
        fetchStats();
      }
    } catch (error) {
      enqueueSnackbar(
        error.response?.data?.message || 'Failed to mark attendance',
        { variant: 'error' }
      );
    }
  };

  const handleStatusChange = (studentId, status) => {
    setAttendanceData(prev => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        status
      }
    }));
  };

  const handleRemarksChange = (studentId, remarks) => {
    setAttendanceData(prev => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        remarks
      }
    }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'present': return 'success';
      case 'absent': return 'error';
      case 'late': return 'warning';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'present': return <PresentIcon />;
      case 'absent': return <AbsentIcon />;
      case 'late': return <LateIcon />;
      default: return <PresentIcon />;
    }
  };

  const TabPanel = ({ children, value, index }) => (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
          Attendance Management
        </Typography>

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Card sx={{ background: 'linear-gradient(135deg, #4caf50 0%, #8bc34a 100%)', color: 'white' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        {stats.totalPresent}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        Present Today
                      </Typography>
                    </Box>
                    <PresentIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Card sx={{ background: 'linear-gradient(135deg, #f44336 0%, #e57373 100%)', color: 'white' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        {stats.totalAbsent}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        Absent Today
                      </Typography>
                    </Box>
                    <AbsentIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Card sx={{ background: 'linear-gradient(135deg, #ff9800 0%, #ffb74d 100%)', color: 'white' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        {stats.totalLate}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        Late Today
                      </Typography>
                    </Box>
                    <LateIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <Card sx={{ background: 'linear-gradient(135deg, #2196f3 0%, #64b5f6 100%)', color: 'white' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        {stats.attendanceRate}%
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        Attendance Rate
                      </Typography>
                    </Box>
                    <AttendanceIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        </Grid>

        {/* Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
            <Tab label="Mark Attendance" />
            <Tab label="View Reports" />
          </Tabs>
        </Paper>

        {/* Mark Attendance Tab */}
        <TabPanel value={tabValue} index={0}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Select Class</InputLabel>
                  <Select
                    value={selectedClass}
                    label="Select Class"
                    onChange={(e) => setSelectedClass(e.target.value)}
                  >
                    {(Array.isArray(classes) ? classes : []).map((classItem) => (
                      <MenuItem key={classItem.class_id} value={classItem.class_id}>
                        {classItem.class_name} - {classItem.section_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <DatePicker
                  label="Select Date"
                  value={selectedDate}
                  onChange={(newValue) => setSelectedDate(newValue)}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={() => {
                      fetchStudents();
                      fetchAttendance();
                    }}
                  >
                    Refresh
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenMarkDialog(true)}
                    disabled={!selectedClass || (Array.isArray(students) ? students : []).length === 0}
                  >
                    Mark Attendance
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Students List */}
          {selectedClass && (
            <Paper>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Student</TableCell>
                      <TableCell>Roll Number</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Remarks</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">Loading...</TableCell>
                      </TableRow>
                    ) : (Array.isArray(students) ? students : []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">No students found</TableCell>
                      </TableRow>
                    ) : (
                      (Array.isArray(students) ? students : []).map((student) => {
                        const attendanceRecord = attendance.find(a => a.student_id === student.student_id);
                        return (
                          <TableRow key={student.student_id} hover>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{ bgcolor: 'primary.main' }}>
                                  {student.first_name?.charAt(0)}{student.last_name?.charAt(0)}
                                </Avatar>
                                <Box>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 'medium' }}>
                                    {student.first_name} {student.last_name}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {student.student_code}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{student.roll_number}</Typography>
                            </TableCell>
                            <TableCell>
                              {attendanceRecord ? (
                                <Chip
                                  icon={getStatusIcon(attendanceRecord.status)}
                                  label={attendanceRecord.status?.charAt(0).toUpperCase() + attendanceRecord.status?.slice(1)}
                                  color={getStatusColor(attendanceRecord.status)}
                                  size="small"
                                />
                              ) : (
                                <Chip label="Not Marked" color="default" size="small" />
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {attendanceRecord?.remarks || '-'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </TabPanel>

        {/* View Reports Tab */}
        <TabPanel value={tabValue} index={1}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Attendance Reports
            </Typography>
            <Typography color="text.secondary">
              Attendance reports and analytics will be implemented here.
            </Typography>
          </Paper>
        </TabPanel>

        {/* Mark Attendance Dialog */}
        <Dialog
          open={openMarkDialog}
          onClose={() => setOpenMarkDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Mark Attendance - {format(selectedDate, 'dd/MM/yyyy')}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              {(Array.isArray(students) ? students : []).map((student) => (
                <Box key={student.student_id} sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      {student.first_name?.charAt(0)}{student.last_name?.charAt(0)}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'medium' }}>
                        {student.first_name} {student.last_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Roll: {student.roll_number}
                      </Typography>
                    </Box>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Status</InputLabel>
                        <Select
                          value={attendanceData[student.student_id]?.status || 'present'}
                          label="Status"
                          onChange={(e) => handleStatusChange(student.student_id, e.target.value)}
                        >
                          <MenuItem value="present">Present</MenuItem>
                          <MenuItem value="absent">Absent</MenuItem>
                          <MenuItem value="late">Late</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Remarks"
                        value={attendanceData[student.student_id]?.remarks || ''}
                        onChange={(e) => handleRemarksChange(student.student_id, e.target.value)}
                        placeholder="Optional remarks"
                      />
                    </Grid>
                  </Grid>
                </Box>
              ))}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenMarkDialog(false)}>Cancel</Button>
            <Button onClick={handleMarkAttendance} variant="contained">
              Save Attendance
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default AttendanceManagement;
